import React, { useState, useEffect } from 'react';

const ElectorateSelector = ({ electorates, states, onElectorateSelect, onCompareElectorates }) => {
  console.log('ElectorateSelector rendering');
  console.log('- electorates:', electorates ? `${electorates.length} items` : 'missing');
  console.log('- states:', states ? `${Object.keys(states).length} items` : 'missing');
  console.log('- onElectorateSelect function:', !!onElectorateSelect);
  
  const [selectedState, setSelectedState] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredElectorates, setFilteredElectorates] = useState([]);
  const [selectedElectorates, setSelectedElectorates] = useState([]);
  const [selectionMode, setSelectionMode] = useState(false);
  
  // Filter electorates when state or search term changes
  useEffect(() => {
    console.log('ElectorateSelector useEffect running');
    
    if (!electorates || electorates.length === 0) {
      console.warn('No electorates data available to filter');
      setFilteredElectorates([]);
      return;
    }
    
    let filtered = [...electorates];
    
    // Filter by state if not 'all'
    if (selectedState !== 'all') {
      filtered = filtered.filter(e => e.state === selectedState);
    }
    
    // Filter by search term if provided
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      filtered = filtered.filter(e => 
        e.name.toLowerCase().includes(term)
      );
    }
    
    // Sort by name
    filtered.sort((a, b) => a.name.localeCompare(b.name));
    
    console.log(`Filtered from ${electorates.length} to ${filtered.length} electorates`);
    setFilteredElectorates(filtered);
  }, [electorates, selectedState, searchTerm]);

  // Toggle electorate selection
  const toggleElectorateSelection = (electorateName) => {
    if (selectedElectorates.includes(electorateName)) {
      setSelectedElectorates(selectedElectorates.filter(name => name !== electorateName));
    } else {
      setSelectedElectorates([...selectedElectorates, electorateName]);
    }
  };

  // Handle single electorate select (when not in selection mode)
  const handleSingleSelect = (electorateName) => {
    if (selectionMode) {
      toggleElectorateSelection(electorateName);
    } else {
      onElectorateSelect(electorateName);
    }
  };

  // Handle compare button click
  const handleCompare = () => {
    if (selectedElectorates.length > 0) {
      onCompareElectorates(selectedElectorates);
    }
  };

  // Toggle selection mode
  const toggleSelectionMode = () => {
    setSelectionMode(!selectionMode);
    if (!selectionMode) {
      // Entering selection mode
      setSelectedElectorates([]);
    }
  };

  return (
    <div className="bg-gray-800 rounded-lg shadow-xl p-6 mb-6 border border-gray-700">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <h2 className="text-2xl font-bold font-sora text-white">Find an Electorate</h2>
        
        <div className="flex items-center space-x-3">
          <button
            onClick={toggleSelectionMode}
            className={`px-4 py-2 rounded-md font-medium transition-colors font-inter ${
              selectionMode 
                ? 'bg-blue-900 text-blue-300 border border-blue-700'
                : 'bg-gray-700 text-gray-300 border border-gray-600 hover:bg-gray-600'
            }`}
          >
            {selectionMode ? 'Cancel Selection' : 'Select Multiple'}
          </button>
          
          {selectionMode && (
            <button
              onClick={handleCompare}
              disabled={selectedElectorates.length < 2}
              className={`px-4 py-2 rounded-md font-medium transition-colors font-inter ${
                selectedElectorates.length < 2
                  ? 'bg-gray-700 text-gray-500 border border-gray-600 cursor-not-allowed'
                  : 'bg-blue-700 text-white hover:bg-blue-600 shadow-md'
              }`}
            >
              Compare ({selectedElectorates.length})
            </button>
          )}
        </div>
      </div>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
        {/* State filter */}
        <div>
          <label htmlFor="state-filter" className="block text-sm font-medium text-gray-300 mb-2 font-inter">
            Filter by State/Territory
          </label>
          <select
            id="state-filter"
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-white font-inter"
          >
            <option value="all">All States & Territories</option>
            {Object.keys(states).sort().map(state => (
              <option key={state} value={state}>{state}</option>
            ))}
          </select>
        </div>
        
        {/* Search box */}
        <div>
          <label htmlFor="search-electorate" className="block text-sm font-medium text-gray-300 mb-2 font-inter">
            Search by Name
          </label>
          <input
            type="text"
            id="search-electorate"
            placeholder="Type to search..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-md shadow-md focus:outline-none focus:ring-blue-500 focus:border-blue-500 text-white font-inter placeholder-gray-400"
          />
        </div>
      </div>
      
      {/* Results count and selection info */}
      <div className="flex justify-between items-center mb-3">
        <p className="text-sm text-gray-400 font-inter">
          {filteredElectorates.length} {filteredElectorates.length === 1 ? 'electorate' : 'electorates'} found
        </p>
        
        {selectionMode && selectedElectorates.length > 0 && (
          <p className="text-sm text-blue-400 font-inter">
            {selectedElectorates.length} selected
          </p>
        )}
      </div>
      
      {/* Electorates list */}
      <div className="bg-gray-750 rounded-md border border-gray-700 overflow-y-auto max-h-60">
        {filteredElectorates.length > 0 ? (
          <ul className="divide-y divide-gray-700">
            {filteredElectorates.map(electorate => {
              const isSelected = selectedElectorates.includes(electorate.name);
              return (
                <li 
                  key={electorate.name}
                  className={`px-5 py-3 cursor-pointer transition-colors ${
                    isSelected 
                      ? 'bg-blue-900 bg-opacity-50' 
                      : 'hover:bg-gray-700'
                  }`}
                  onClick={() => handleSingleSelect(electorate.name)}
                >
                  <div className="flex justify-between items-center">
                    <div className="flex items-center space-x-3">
                      {selectionMode && (
                        <div className={`w-5 h-5 flex-shrink-0 border rounded ${
                          isSelected 
                            ? 'bg-blue-600 border-blue-500 flex items-center justify-center' 
                            : 'border-gray-500'
                        }`}>
                          {isSelected && (
                            <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                          )}
                        </div>
                      )}
                      <div>
                        <h3 className="font-medium text-blue-300 font-inter">{electorate.name}</h3>
                        <p className="text-sm text-gray-400 font-inter">{electorate.state}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-green-300 font-inter">${electorate.totalValue.toLocaleString()}</p>
                      <p className="text-xs text-gray-400 font-inter">Total Grants</p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        ) : (
          <div className="p-6 text-center text-gray-400 font-inter">
            No electorates match your search criteria
          </div>
        )}
      </div>
    </div>
  );
};

export default ElectorateSelector;